import { useState, useCallback, useMemo } from 'react';
import type { AcademySport, SkillRating, StudentProgress } from '@court-sync/shared';
import { studentProgressService } from '@/services/studentProgressService';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Loader2, PlusCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  formatAssessmentSkillLabel,
  getAcademySportDisplayLabel,
  getAssessmentSkillKeysForAcademySport,
  resolveAcademySport,
} from '@/lib/sportPresentation';

type RatingOrUnset = '' | SkillRating;

function emptyPerSkill(skillKeys: readonly string[]): Record<string, RatingOrUnset> {
  return Object.fromEntries(skillKeys.map((key) => [key, '' as RatingOrUnset]));
}

function loadPerSkillFromProgress(
  p: StudentProgress,
  skillKeys: readonly string[]
): Record<string, RatingOrUnset> {
  const next = emptyPerSkill(skillKeys);
  for (const key of skillKeys) {
    const rating = (p.skillRatings as Record<string, SkillRating | undefined>)[key];
    if (rating != null) next[key] = rating;
  }
  return next;
}

export interface CoachSportProgressFormProps {
  studentId: string;
  academyId: string;
  academySports: readonly AcademySport[];
  /** Defaults to primary academy sport when unset. */
  academySportId?: string | null;
  progress: StudentProgress | null;
  onSaved: () => void | Promise<void>;
}

export function CoachSportProgressForm({
  studentId,
  academyId,
  academySports,
  academySportId,
  progress,
  onSaved,
}: CoachSportProgressFormProps) {
  const resolvedSport = useMemo(
    () => resolveAcademySport(academySports, academySportId),
    [academySports, academySportId]
  );
  const skillKeys = useMemo(
    () => getAssessmentSkillKeysForAcademySport(resolvedSport),
    [resolvedSport]
  );
  const sportLabel = resolvedSport ? getAcademySportDisplayLabel(resolvedSport) : 'Activity';

  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState<'add' | 'update'>('add');
  const [skillLevel, setSkillLevel] = useState<'beginner' | 'intermediate' | 'advanced'>('intermediate');
  const [perSkill, setPerSkill] = useState<Record<string, RatingOrUnset>>(() => emptyPerSkill(skillKeys));
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const resetFormToDefaults = useCallback(() => {
    setSkillLevel('intermediate');
    setNotes('');
    setPerSkill(emptyPerSkill(skillKeys));
  }, [skillKeys]);

  const openAddModal = () => {
    setEditMode('add');
    resetFormToDefaults();
    setModalOpen(true);
  };

  const openUpdateModal = () => {
    if (!progress) return;
    setEditMode('update');
    setSkillLevel(progress.skillLevel);
    setNotes(progress.notes ?? '');
    setPerSkill(loadPerSkillFromProgress(progress, skillKeys));
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    resetFormToDefaults();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const skillRatings: Record<string, SkillRating> = {};
      for (const key of skillKeys) {
        const value = perSkill[key];
        if (value !== '') {
          skillRatings[key] = value;
        }
      }

      const payload = {
        academyId,
        skillLevel,
        skillRatings,
        notes: notes.trim() || undefined,
        ...(resolvedSport?.id && !resolvedSport.id.startsWith('fallback-')
          ? { academySportId: resolvedSport.id }
          : {}),
      };

      if (!progress) {
        await studentProgressService.createProgress({
          studentId,
          ...payload,
        });
        toast.success('Assessment saved');
      } else {
        await studentProgressService.updateProgress(studentId, payload);
        toast.success('Assessment updated');
      }
      await onSaved();
      closeModal();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
          : null;
      toast.error(msg || (err instanceof Error ? err.message : 'Could not save assessment'));
    } finally {
      setSaving(false);
    }
  };

  const submitLabel =
    !progress || editMode === 'add' ? 'Save assessment' : 'Update assessment';

  return (
    <Card className="border-dashed">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold">{sportLabel} activity assessment</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center gap-3">
        <Button type="button" onClick={openAddModal}>
          <PlusCircle className="mr-2 h-4 w-4" />
          Add assessment
        </Button>
        {progress ? (
          <Button type="button" variant="outline" onClick={openUpdateModal}>
            Update current snapshot
          </Button>
        ) : null}
      </CardContent>

      <Dialog open={modalOpen} onOpenChange={(open) => { if (!open) closeModal(); }}>
        <DialogContent className="max-h-[min(90vh,720px)] overflow-y-auto sm:max-w-lg" aria-describedby="sport-assess-modal-desc">
          <DialogHeader>
            <DialogTitle>
              {!progress ? 'Record assessment' : editMode === 'add' ? 'Add assessment' : 'Update assessment'}
            </DialogTitle>
            <DialogDescription id="sport-assess-modal-desc">
              Set overall level and optional 1–5 ratings per skill for {sportLabel}. Saves the activity
              snapshot and adds a row to Progress History.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
            <div>
              <Label htmlFor="sport-modal-skill-level" className="mb-1.5 block">
                Overall level
              </Label>
              <select
                id="sport-modal-skill-level"
                value={skillLevel}
                onChange={(e) =>
                  setSkillLevel(e.target.value as 'beginner' | 'intermediate' | 'advanced')
                }
                className="w-full max-w-xs rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {skillKeys.map((skill) => (
                <div key={skill}>
                  <Label htmlFor={`sport-modal-${skill}`} className="mb-1 block text-xs font-normal">
                    {formatAssessmentSkillLabel(skill)}
                  </Label>
                  <select
                    id={`sport-modal-${skill}`}
                    value={perSkill[skill] === '' ? '' : String(perSkill[skill])}
                    onChange={(e) => {
                      const v = e.target.value;
                      setPerSkill((prev) => ({
                        ...prev,
                        [skill]: v === '' ? '' : (Number(v) as SkillRating),
                      }));
                    }}
                    className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
                  >
                    <option value="">—</option>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            <div>
              <Label htmlFor="sport-modal-notes" className="mb-1.5 block">
                Notes (optional)
              </Label>
              <Textarea
                id="sport-modal-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Session observations…"
                className="resize-y min-h-[72px]"
              />
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={closeModal} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  submitLabel
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
